"use client";

import React from "react";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { FileText, Download, Settings, ShieldCheck, CircleCheck, CircleMinus } from "lucide-react";
const comparisonData = [{
  feature: "Signal Attenuation",
  aqua: "<0.18 dB/km @ 1550nm (Ultra-low)",
  traditional: ">0.22 dB/km @ 1550nm (Standard)"
}, {
  feature: "Bending Resilience",
  aqua: "G.657.A2 Macro-bend optimized",
  traditional: "Standard G.652.D (High bend loss)"
}, {
  feature: "Thermal Stability",
  aqua: "-60°C to +85°C (Extreme environments)",
  traditional: "-40°C to +70°C (Standard grade)"
}, {
  feature: "Data Throughput",
  aqua: "800G / Terabit Ethernet Ready",
  traditional: "100G Capacity Limited"
}, {
  feature: "Frame Durability",
  aqua: "3K Twill Carbon (Aerospace Grade)",
  traditional: "Injection Molded Industrial Plastic"
}];
export default function TechnicalSpecsDownloads() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="173501" id="technical-documentation" className="dark bg-background text-foreground relative overflow-hidden py-20 md:py-32">
      {}
      <div className="absolute inset-0 z-0 opacity-10 pointer-events-none bg-[url(https://wpvc-images.s3.us-east-1.amazonaws.com/images/1604101/img/blueprint_technical_watermark_bg.png)] bg-cover bg-center mix-blend-overlay" />

      <div ref={ref} className={`container mx-auto px-4 relative z-10 transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
        <div className="mb-16 text-center lg:text-left max-w-3xl">
          <h2 className="text-3xl md:text-5xl font-bold mb-6 tracking-tighter text-foreground">
            Technical Specifications &amp; Resources
          </h2>
          <p className="text-muted-foreground text-lg md:text-xl font-sans">
            Review the engineered advantages of Nexel Technologies' optical solutions and 
            FPV systems. Access comprehensive datasheets and compliance documentation 
            for mission-critical infrastructure.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
          {}
          <div className="lg:col-span-7 space-y-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="bg-primary/10 p-2 rounded-lg border border-primary/20">
                <Settings className="text-primary size-6" />
              </div>
              <h3 className="text-2xl font-bold tracking-tight text-foreground uppercase">Performance Benchmarks</h3>
            </div>

            <div className="rounded-xl border border-border bg-card/40 backdrop-blur-xl overflow-hidden shadow-2xl">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/50 hover:bg-muted/50 border-border">
                    <TableHead className="w-[180px] py-4 text-foreground font-bold uppercase text-xs tracking-widest">
                      Metric
                    </TableHead>
                    <TableHead className="py-4 text-primary font-bold text-base">
                      Nexel Standard
                    </TableHead>
                    <TableHead className="py-4 text-muted-foreground font-medium">
                      Industry Average
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {comparisonData.map((row, i) => <TableRow key={i} data-index={i} className="border-border hover:bg-primary/5 transition-colors">
                      <TableCell className="font-semibold py-4 text-foreground/90">
                        {row.feature}
                      </TableCell>
                      <TableCell className="py-4 font-mono text-sm">
                        <div className="flex items-start gap-2 text-primary">
                          <CircleCheck className="size-5 text-primary shrink-0 mt-0.5" />
                          <span>{row.aqua}</span>
                        </div>
                      </TableCell>
                      <TableCell className="py-4 font-mono text-sm">
                        <div className="flex items-start gap-2 text-muted-foreground">
                          <CircleMinus className="size-5 opacity-40 shrink-0 mt-0.5" />
                          <span>{row.traditional}</span>
                        </div>
                      </TableCell>
                    </TableRow>)}
                </TableBody>
              </Table>
            </div>
          </div>

          {}
          <div className="lg:col-span-5 lg:pl-6">
            <Card className="bg-card border-border shadow-2xl relative overflow-hidden h-full flex flex-col backdrop-blur-sm">
              {}
              <div className="absolute top-0 right-0 w-40 h-40 bg-primary/5 rounded-bl-full pointer-events-none" />
              <div className="absolute -bottom-10 -left-10 w-32 h-32 bg-primary/5 rounded-tr-full pointer-events-none" />

              <CardHeader className="pb-8 relative z-10">
                <div className="bg-primary/10 w-12 h-12 rounded-lg flex items-center justify-center mb-6 border border-primary/20">
                  <FileText className="text-primary size-6" />
                </div>
                <CardTitle className="text-3xl font-bold mb-3 tracking-tight">
                  Resource Hub
                </CardTitle>
                <CardDescription className="text-base text-muted-foreground leading-relaxed font-sans">
                  Download precise optical specifications, flight controller manuals,
                  and connectivity schematics optimized for global standards.
                </CardDescription>
              </CardHeader>

              <CardContent className="space-y-5 flex-grow flex flex-col justify-end relative z-10">
                <Button size="lg" className="w-full h-14 justify-between group text-base font-bold shadow-lg shadow-primary/10 bg-primary hover:bg-primary/90 text-primary-foreground" asChild>
                  <Link to="https://galabit.com/wp-content/uploads/2026/04/G.657.A2-Datasheet.pdf" newTab>
                    <span className="flex items-center gap-3">
                      <Download className="size-5 group-hover:-translate-y-1 transition-transform duration-300" />
                      Download Full Catalog
                    </span>
                    <Badge variant="secondary" className="bg-primary-foreground/10 text-primary-foreground hover:bg-primary-foreground/20 border-transparent font-mono">
                      PDF
                    </Badge>
                  </Link>
                </Button>

                <Button size="lg" variant="outline" className="w-full h-14 justify-start group border-border hover:border-primary/50 hover:bg-primary/5 text-base font-semibold" asChild>
                  <Link to="/SupportPage">
                    <ShieldCheck className="size-5 text-primary mr-3 group-hover:scale-110 transition-transform duration-300" />
                    Technical Data Sheet
                  </Link>
                </Button>
                
                <p className="text-xs text-center text-muted-foreground mt-4 pt-4 border-t border-border/50 font-mono uppercase tracking-wider">
                  Compliance: ITU-T G.652.D / G.657.A1 / G.657.A2
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>;
}